import { Textarea } from '@/components/ui/textarea';
import { AfricanKeyboard } from '@/components/keyboard';
import { TaskItemCard, TaskNotice } from '@/features/contribution-kit';
import { Keyboard } from 'lucide-react';
import type { translations } from '@/translations/translations';
import type { TranscriptionAudioCopy } from './transcription-audio-copy';

/** The shared strings this card reads, typed so a renamed key fails the build. */
export type TranscriptionAudioWriteStrings = Pick<
  (typeof translations)['fr'],
  | 'rejectedAttemptBannerTitle'
  | 'rejectedAttemptBannerDesc'
  | 'rejectedAttemptReasonsLabel'
  | 'placehoderTranscriptionAudio'
  | 'optionalFrenchTranslationLabel'
  | 'optionalFrenchTranslationPlaceholder'
  | 'africanKeyboard'
>;

type TranscriptionAudioWriteCardProps = {
  t: TranscriptionAudioWriteStrings;
  copy: TranscriptionAudioCopy;
  text: string;
  onTextChange: (value: string) => void;
  textFrancais: string;
  onTextFrancaisChange: (value: string) => void;
  showKeyboard: boolean;
  onToggleKeyboard: () => void;
  isLoading: boolean;
  hasRejectedAttempt: boolean;
  rejectionReasons: string[];
};

/**
 * What the person writes: the transcription in the spoken language (the
 * African keyboard opens when the field gets focus) and an optional French
 * translation. A previous rejected attempt is explained above the fields.
 * Also used by the initial-data transcription (/initial-transcription).
 */
export function TranscriptionAudioWriteCard({
  t,
  copy,
  text,
  onTextChange,
  textFrancais,
  onTextFrancaisChange,
  showKeyboard,
  onToggleKeyboard,
  isLoading,
  hasRejectedAttempt,
  rejectionReasons,
}: TranscriptionAudioWriteCardProps) {
  return (
    <TaskItemCard label={copy.writeLabel}>
      {hasRejectedAttempt ? (
        <TaskNotice tone="warning" title={t.rejectedAttemptBannerTitle}>
          <p>{t.rejectedAttemptBannerDesc}</p>
          {rejectionReasons.length > 0 ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-foreground">{t.rejectedAttemptReasonsLabel}</span>
              {rejectionReasons.map((reason) => (
                <span key={reason} className="rounded-full border border-warning/30 px-2 py-0.5 text-xs text-warning">
                  {reason}
                </span>
              ))}
            </div>
          ) : null}
        </TaskNotice>
      ) : null}

      <Textarea
        value={text}
        aria-label={copy.writeLabel}
        onFocus={() => {
          if (!showKeyboard) onToggleKeyboard();
        }}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder={t.placehoderTranscriptionAudio}
        disabled={isLoading}
        className="min-h-[120px] bg-surface text-base"
      />

      <div className="space-y-2">
        <label htmlFor="transcription-audio-french" className="text-sm font-medium text-muted-foreground">
          {t.optionalFrenchTranslationLabel}
        </label>
        <Textarea
          id="transcription-audio-french"
          value={textFrancais}
          onChange={(e) => onTextFrancaisChange(e.target.value)}
          placeholder={t.optionalFrenchTranslationPlaceholder}
          disabled={isLoading}
          className="min-h-[96px] bg-surface text-base"
        />
      </div>

      <button
        type="button"
        onClick={onToggleKeyboard}
        aria-pressed={showKeyboard}
        className="inline-flex h-11 items-center gap-2 rounded-md px-2 text-sm text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Keyboard className="size-4" strokeWidth={1.6} aria-hidden="true" />
        <span>{t.africanKeyboard}</span>
        <span className="sr-only">{showKeyboard ? copy.keyboardHide : copy.keyboardShow}</span>
        <span className={`size-1.5 rounded-full ${showKeyboard ? 'bg-primary' : 'bg-border'}`} aria-hidden="true" />
      </button>

      {showKeyboard ? (
        <div className="border-t border-border/60 pt-4">
          <AfricanKeyboard language="generic" value={text} onChange={onTextChange} onClose={onToggleKeyboard} />
        </div>
      ) : null}
    </TaskItemCard>
  );
}
